<template>
  <div class="preview-backdrop">
    <transition name="fade">
      <div class="initial-loader">
        <Spinner color="teal" v-show="!displayPreviewContainer"/>
      </div>
    </transition> 
    <div
      class="preview-container"
      :style="{ opacity: displayPreviewContainer ? 1 : 0, filter: this.joinGameErrorMsg.length > 0 ? 'blur(4px)' : '' }"
    >
      <div class="preview-title">
        Game Setup
      </div>
      <div class="preview-tracks-wrapper">
        <VideoAndAudioPreview
          ref="videoAndAudioPreview"
          :redirectedFromCorrectPage="wasRedirectedFromGameEntrance"
          @display-connect-video-modal="displayConnectVideoModal = $event"
          @load-local-tracks="localTracks = $event"
        />
      </div>
      <div class="preview-name-container">
        <FirstNameInput 
          v-model="firstName"
          :characterLimit="characterLimit"
        />
      </div>
      <div class="preview-play-music-container">
        <PlayMusicCheckbox 
          :value="isPlayMusicOptionChecked"
          @change="isPlayMusicOptionChecked = $event"
        />
      </div>
      <div class="preview-submit-container">
        <SpinnerButton
          class="preview-action-button"
          :isProcessing="isEnterRoomButtonClicked"
          :isDisabled="firstName.length > characterLimit"
          @click="enterRoom"
        >
          Enter Room
        </SpinnerButton>
      </div>
    </div>
    <transition name="pop-from-top">
      <SetupErrorModal 
        v-if="joinGameErrorMsg.length > 0"
        :errorMsg="joinGameErrorMsg"
        @click="returnToGameEntrance"
      />
    </transition>
    <transition name="pop-from-top">
      <EnableVideoModal
        v-if="displayConnectVideoModal && $refs.videoAndAudioPreview"
        @click="$refs.videoAndAudioPreview.getAndAttachLocalTracks"
      />
    </transition>
  </div>
</template>

<script>
import EnableVideoModal from '@/components/gameComponents/gameSetupComponents/EnableVideoModal';
import FirstNameInput from '@/components/gameComponents/gameSetupComponents/FirstNameInput';
import PlayMusicCheckbox from '@/components/gameComponents/gameSetupComponents/PlayMusicCheckbox';
import SetupErrorModal from '@/components/gameComponents/gameSetupComponents/SetupErrorModal';
import VideoAndAudioPreview from '@/components/gameComponents/gameSetupComponents/VideoAndAudioPreview';
import Spinner from '@/components/utilComponents/Spinner';
import SpinnerButton from '@/components/utilComponents/SpinnerButton';

export default {
  name: 'GameSetupScreen',
  components: {
    EnableVideoModal,
    FirstNameInput,
    PlayMusicCheckbox,
    SetupErrorModal,
    VideoAndAudioPreview,
    Spinner,
    SpinnerButton
  },
  props: {
    joinGameRoomcode: String,
    wasRedirectedFromGameEntrance: Boolean
  },
  data: () => ({
    localTracks: [],
    firstName: '',
    defaultFirstName: '',
    displayPreviewContainer: false,
    isPlayMusicOptionChecked: true,
    isEnterRoomButtonClicked: false,
    joinGameErrorMsg: '',
    // joinGameErrorMsg: 'an error message',
    characterLimit: 16,
    displayConnectVideoModal: false
  }),
  computed: {
    auth0UserId: function() {
      return this.$auth.user.sub.replace(/\|/g, '_');
    },
    idToken: function () {
      return this.$auth.idToken;
    }
  },
  methods: {
    async getFirstName() {
      try {
        const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/getfirstname`, {
          auth0UserId: this.auth0UserId
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });

        this.defaultFirstName = response.data.firstName.slice(0, this.characterLimit);
        this.firstName = this.defaultFirstName;
      } catch (error) {
        console.log('Error:', error);
      }
    },
    async updateFirstName() {
      return this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/updatefirstname`, {
        auth0UserId: this.auth0UserId,
        firstName: this.firstName
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      });
    },
    async enterRoom() {
      this.isEnterRoomButtonClicked = true;
      try {
        if (this.firstName.length > 0 && this.firstName.length <= this.characterLimit) {
          await this.updateFirstName();
        } else {
          this.firstName = this.defaultFirstName;
        }
        if (this.joinGameRoomcode && this.joinGameRoomcode.length > 0) {
          this.joinGame();
        } else {
          this.createGame();
        }
      } catch (error) {
        console.log('Error:', error);
        this.isEnterRoomButtonClicked = false;
      }
    },
    joinGame() {
      this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/joingame`, {
        auth0UserId: this.auth0UserId,
        gameRoomcode: this.joinGameRoomcode
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      }).then(response => {
        if ('errorMsg' in response.data) {
          this.isEnterRoomButtonClicked = false;
          this.joinGameErrorMsg = response.data.errorMsg;
        } else {
          this.$router.push({
            name: 'weavr',
            params: {
              initialGameRoomcode: this.joinGameRoomcode,
              videoRoomcode: response.data.videoRoomcode,
              localTracks: this.localTracks,
              isMusicMutedInitially: !this.isPlayMusicOptionChecked,
              wasRedirectedFromGameSetupScreen: true
            }
          });
        }
      }).catch(error => {
        console.log(error);
        this.isEnterRoomButtonClicked = false;
      });
    },
    createGame() {
      this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/creategame`, {
        auth0UserId: this.auth0UserId
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      }).then(response => {
        if (response.status === 200) {
          this.$router.push({
            name: 'weavr',
            params: {
              initialGameRoomcode: response.data.gameRoomcode,
              videoRoomcode: response.data.videoRoomcode,
              localTracks: this.localTracks,
              isMusicMutedInitially: !this.isPlayMusicOptionChecked,
              wasRedirectedFromGameSetupScreen: true
            }
          });
        }
      }).catch(error => {
        console.log(error);
        this.isEnterRoomButtonClicked = false;
      });
    },
    returnToGameEntrance() {
      this.$router.push({
        name: 'home',
      });
    }
  },
  beforeRouteLeave(to, from, next) {
    if (to.name !== 'weavr') {
      console.log(this.localTracks);
      this.localTracks.forEach(track => {
        track.stop();
      });
    }
    next();
  },
  created: function() {
    if (!this.wasRedirectedFromGameEntrance) {
      this.returnToGameEntrance();
    }
  },
  mounted: function() {
    this.$nextTick(async function() {
      if (this.wasRedirectedFromGameEntrance) {
        await this.getFirstName();
        this.displayPreviewContainer = true;
      }
    });
  },
}
</script>

<style scoped lang="scss">
@import '@/styles/setup-styles.scss';
</style>
